import { useTranslation } from 'react-i18next';
import PageHero from '../../components/shared/PageHero';
import Sidebar from '../../components/shared/Sidebar';
import { BookOpen, Search, Clock, Wifi, Printer, ChevronRight, ExternalLink } from 'lucide-react';

export default function Library() {
  const { t } = useTranslation();

  const services = [
    { icon: <Search className="w-6 h-6" />, titleKey: 'library_service_catalog', href: 'https://library.estad.edu/catalog' },
    { icon: <BookOpen className="w-6 h-6" />, titleKey: 'library_service_digital', href: 'https://library.estad.edu/digital' },
    { icon: <Wifi className="w-6 h-6" />, titleKey: 'library_service_spaces', href: '/library/spaces' },
    { icon: <Printer className="w-6 h-6" />, titleKey: 'library_service_printing', href: '/library/printing' }
  ];

  return (
    <div className="flex flex-col bg-white">
      <PageHero 
        title={t('nav_library', 'Library')}
        description={t('library_hero_desc', 'Access world-class resources, study spaces, and research support across all ESTAD campuses.')}
        breadcrumbs={[{ label: t('nav_library', 'Library'), href: '/library' }]}
        variant="light"
      />

      <main className="max-w-7xl mx-auto w-full py-20 px-6 lg:px-12 flex flex-col lg:flex-row gap-16">
        <Sidebar variant="programs" />

        <div className="flex-1 space-y-24 animate-in fade-in slide-in-from-right-8 duration-700 delay-200">

          {/* Hero image */}
          <div className="w-full h-56 md:h-80 overflow-hidden rounded-sm shadow-2xl border-b-8 border-gold">
            <img src="/images/campus/classroom-01.jpg" alt="ESTAD Library" className="w-full h-full object-cover" />
          </div>

          {/* Services Grid */}
          <section id="services" className="space-y-12">
            <h2 className="text-3xl font-black text-navy uppercase tracking-tight border-b-2 border-gray-100 pb-4 flex items-center gap-4 italic">
              <BookOpen className="w-8 h-8 text-gold" /> {t('library_services_title', 'Library Services')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {services.map((service, idx) => (
                <a 
                  key={idx} 
                  href={service.href} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="group bg-gray-50 p-8 border-l-8 border-navy hover:bg-navy hover:text-white transition-all duration-300 rounded-sm shadow-sm hover:shadow-xl"
                >
                  <div className="flex justify-between items-start mb-6">
                    <div className="w-12 h-12 rounded-lg bg-white shadow-sm flex items-center justify-center text-navy group-hover:scale-110 transition-transform">
                      {service.icon}
                    </div>
                    <ExternalLink className="w-4 h-4 opacity-30 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <h3 className="text-xl font-black uppercase tracking-tight mb-2 leading-none">
                    {t(service.titleKey, service.titleKey)}
                  </h3>
                  <p className="text-gray-500 group-hover:text-white/70 text-sm font-medium leading-relaxed">
                    Explore our collection of resources and services designed to support your academic success.
                  </p>
                </a>
              ))}
            </div>
          </section>

          {/* Locations & Hours */}
          <section id="locations" className="space-y-12">
            <h2 className="text-3xl font-black text-navy uppercase tracking-tight border-b-2 border-gray-100 pb-4 flex items-center gap-4 italic">
              <Clock className="w-8 h-8 text-gold" /> {t('library_locations_title', 'Locations & Hours')}
            </h2>
            <div className="space-y-6">
              {[
                { name: 'Yaoundé Main Library', hours: 'Mon - Fri: 7:30 AM - 10:00 PM', location: 'Palais des Congrès, Yaoundé' },
                { name: 'Douala Tech Library', hours: 'Mon - Fri: 8:00 AM - 8:00 PM', location: 'Bonanjo, Douala' }
              ].map((loc, idx) => (
                <div key={idx} className="flex flex-col md:flex-row md:items-center justify-between p-8 bg-offWhite border border-gray-100 group hover:border-gold transition-all">
                  <div className="space-y-2">
                    <h3 className="text-xl font-black text-navy uppercase tracking-tighter group-hover:text-gold transition-colors">{loc.name}</h3>
                    <p className="text-gray-400 text-xs font-bold uppercase tracking-widest">{loc.location}</p>
                  </div>
                  <div className="mt-4 md:mt-0 flex items-center gap-3 text-navy font-black text-sm">
                     <Clock className="w-4 h-4 text-gold" /> {loc.hours}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Help Banner */}
          <section id="help" className="bg-navy p-12 md:p-16 text-white rounded-sm shadow-2xl relative overflow-hidden flex flex-col md:flex-row items-center justify-between gap-10 group">
             <div className="absolute inset-0 bg-gold/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
             <div className="relative z-10 text-center md:text-left space-y-4">
                <h2 className="text-3xl font-black uppercase tracking-tighter italic leading-none">{t('library_help_title', 'Need Research Help?')}</h2>
                <p className="text-white/60 font-medium max-w-md">{t('library_help_desc', 'Our librarians are available to assist you with your research needs.')}</p>
             </div>
             <div className="relative z-10 flex flex-wrap justify-center gap-4">
               <button className="bg-gold text-navy px-10 py-4 font-black uppercase tracking-widest text-xs shadow-lg hover:brightness-110 transition-all active:scale-95">
                 Ask a Librarian
               </button>
               <button className="border-2 border-white/20 text-white px-10 py-4 font-black uppercase tracking-widest text-xs hover:bg-white hover:text-navy transition-all active:scale-95">
                 Contact Us
               </button>
             </div>
          </section>

        </div>
      </main>
    </div>
  );
}
